import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Loader2, Zap } from 'lucide-react';
import { submitBooking, buildWhatsAppLink } from '../mock';
import { toast } from 'sonner';

export const QuickQuote = ({ language }) => {
  const t = (en, pt) => (language === 'en' ? en : pt);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [data, setData] = useState({
    name: '', phone: '', email: '', service: '', passengers: '', pickup: '', destination: '', date: ''
  });

  const handle = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await submitBooking({ ...data, source: 'quick-quote' });
      if (res.success) {
        toast.success(t(`Quote request sent! Ref: ${res.bookingId}. We'll reply shortly.`, `Pedido enviado! Ref: ${res.bookingId}. Respondemos em breve.`));
        // Optionally also open WhatsApp pre-filled (only if the customer opted in)
        if (sendWhatsApp) {
          window.open(buildWhatsAppLink(data, res.bookingId), '_blank');
        }
        setData({ name: '', phone: '', email: '', service: '', passengers: '', pickup: '', destination: '', date: '' });
        setSendWhatsApp(false);
      }
    } catch (err) {
      toast.error(t('Something went wrong. Please try again.', 'Ocorreu um erro. Tente novamente.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-sm shadow-2xl border border-[#E2E8F0] p-6 md:p-7" data-testid="quick-quote-card">
      <div className="flex items-center gap-2 mb-1">
        <Zap className="w-5 h-5 text-[#D4AF37]" />
        <h3 className="heading-font text-2xl font-medium text-[#0F172A]">{t('Get a Free Quote', 'Peça um Orçamento Grátis')}</h3>
      </div>
      <p className="text-sm text-[#475569] font-light mb-5">{t('Reply in minutes · No obligation', 'Resposta em minutos · Sem compromisso')}</p>

      <form onSubmit={submit} className="space-y-4" data-testid="quick-quote-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="qq-name" className="text-xs">{t('Name', 'Nome')} *</Label>
            <Input id="qq-name" name="name" data-testid="qq-name" value={data.name} onChange={handle} required placeholder={t('Your name', 'O seu nome')} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="qq-phone" className="text-xs">{t('Phone / WhatsApp', 'Telefone / WhatsApp')} *</Label>
            <Input id="qq-phone" name="phone" type="tel" data-testid="qq-phone" value={data.phone} onChange={handle} required placeholder="+351 ..." className="mt-1" />
          </div>
        </div>

        <div>
          <Label htmlFor="qq-email" className="text-xs">{t('Email', 'E-mail')} *</Label>
          <Input id="qq-email" name="email" type="email" data-testid="qq-email" value={data.email} onChange={handle} required placeholder="email@exemplo.com" className="mt-1" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="qq-service" className="text-xs">{t('Service', 'Serviço')} *</Label>
            <Select value={data.service} onValueChange={(v) => setData({ ...data, service: v })} required>
              <SelectTrigger id="qq-service" data-testid="qq-service" className="mt-1">
                <SelectValue placeholder={t('Select', 'Selecione')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lowcost">{t('Low Cost Transfer', 'Transfer Low Cost')}</SelectItem>
                <SelectItem value="luxury">{t('Luxury Transfer', 'Transfer Luxury')}</SelectItem>
                <SelectItem value="van">{t('Van Transfer', 'Transfer Van')}</SelectItem>
                <SelectItem value="private-tour">{t('Private Tour', 'Tour Privado')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="qq-date" className="text-xs">{t('Date', 'Data')} *</Label>
            <Input id="qq-date" name="date" type="date" data-testid="qq-date" value={data.date} onChange={handle} required className="mt-1" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="qq-pickup" className="text-xs">{t('Pickup', 'Recolha')} *</Label>
            <Input id="qq-pickup" name="pickup" data-testid="qq-pickup" value={data.pickup} onChange={handle} required placeholder={t('Airport, hotel...', 'Aeroporto, hotel...')} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="qq-destination" className="text-xs">{t('Destination', 'Destino')}</Label>
            <Input id="qq-destination" name="destination" data-testid="qq-destination" value={data.destination} onChange={handle} placeholder={t('Optional', 'Opcional')} className="mt-1" />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Checkbox id="qq-whatsapp" data-testid="qq-whatsapp" checked={sendWhatsApp} onCheckedChange={(v) => setSendWhatsApp(!!v)} />
          <Label htmlFor="qq-whatsapp" className="text-sm font-light text-[#475569] cursor-pointer">
            {t('Also send my request via WhatsApp', 'Enviar também o meu pedido por WhatsApp')}
          </Label>
        </div>

        <Button type="submit" disabled={isSubmitting} data-testid="qq-submit" className="w-full bg-[#D4AF37] hover:bg-[#B48E2D] text-white rounded-sm py-6 text-base font-medium transition-colors shadow-sm">
          {isSubmitting ? (
            <><Loader2 className="mr-2 h-5 w-5 animate-spin" />{t('Sending...', 'A enviar...')}</>
          ) : (
            t('Get My Free Quote', 'Receber Orçamento Grátis')
          )}
        </Button>
        <p className="text-center text-xs text-[#475569] font-light">{t('Takes less than 30 seconds', 'Demora menos de 30 segundos')}</p>
      </form>
    </div>
  );
};
